import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import {
  map,
  catchError,
  switchMap,
  tap,
  filter,
  finalize,
  withLatestFrom,
} from 'rxjs/operators';
import { CompanyContactsActions } from './company-contacts.actions';
import { ContactsService } from '@src/app/core/services/contacts/contacts.service';
import { Store } from '@ngrx/store';
import { AuthSelectors } from '../auth/auth.selectors';

@Injectable()
export class CompanyContactsEffects {
  private actions$ = inject(Actions);
  private contactsService = inject(ContactsService);
  private store = inject(Store);
  private loadingCompanyId: string | null = null;

  loadContacts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyContactsActions.loadCompanyContacts),
      withLatestFrom(this.store.select(AuthSelectors.selectSelectedCompany)),
      filter(([_, company]) => {
        if (!company || !company.CLIEN_NM) {
          return false;
        }
        if (this.loadingCompanyId === company.CLIEN_NM) {
          return false;
        }
        return true;
      }),
      tap(([_, company]) => {
        this.loadingCompanyId = company!.CLIEN_NM;
      }),
      switchMap(() =>
        this.contactsService.getCompanyContacts().pipe(
          map((response) => {
            const contacts = response ?? [];
            return CompanyContactsActions.loadCompanyContactsSuccess({
              contacts,
            });
          }),
          catchError((error) => {
            return of(
              CompanyContactsActions.loadCompanyContactsFailure({ error }),
            );
          }),
          finalize(() => {
            this.loadingCompanyId = null;
          }),
        ),
      ),
    ),
  );

  updateProfiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyContactsActions.updateContactProfiles),
      switchMap(({ contactId, profiles }) =>
        this.contactsService.updateContactProfiles(contactId, profiles).pipe(
          switchMap((response) => {
            // Since API doesn't return updated contact, reload all contacts
            if (response && response.success) {
              return this.contactsService.getCompanyContacts(true).pipe(
                map((contacts) => {
                  return CompanyContactsActions.loadCompanyContactsSuccess({
                    contacts,
                  });
                }),
                catchError((error) => {
                  console.error('❌ Effects: Error reloading contacts:', error);
                  return of(
                    CompanyContactsActions.loadCompanyContactsFailure({
                      error,
                    }),
                  );
                }),
              );
            } else {
              throw new Error('Profile update was not successful');
            }
          }),
          catchError((error) => {
            console.error(
              '❌ Effects: Error updating contact profiles:',
              error,
            );
            return of(
              CompanyContactsActions.updateContactProfilesFailure({ error }),
            );
          }),
        ),
      ),
    ),
  );

  updateContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyContactsActions.updateCompanyContact),
      switchMap(({ contact }) =>
        this.contactsService
          .updateContact(contact.id, {
            nombre: contact.nombre,
            apellido1: contact.apellido1,
            apellido2: contact.apellido2,
            email: contact.email ?? '',
            telefono: contact.telefono,
            telefono_movil: contact.movil,
            cargo: contact.cargo,
          })
          .pipe(
            switchMap(() => {
              // Reload all contacts after update
              return this.contactsService.getCompanyContacts(true).pipe(
                map((contacts) => {
                  return CompanyContactsActions.loadCompanyContactsSuccess({
                    contacts,
                  });
                }),
                catchError((error) => {
                  console.error('❌ Effects: Error reloading contacts:', error);
                  return of(
                    CompanyContactsActions.loadCompanyContactsFailure({
                      error,
                    }),
                  );
                }),
              );
            }),
            catchError((error) => {
              console.error('❌ Effects: Error updating contact:', error);
              return of(
                CompanyContactsActions.updateCompanyContactFailure({ error }),
              );
            }),
          ),
      ),
    ),
  );

  deleteContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyContactsActions.deleteCompanyContact),
      switchMap(({ contactId }) =>
        this.contactsService.removeContact(contactId).pipe(
          map(() => {
            return CompanyContactsActions.deleteCompanyContactSuccess({
              contactId,
            });
          }),
          catchError((error) => {
            console.error('❌ Effects: Error deleting contact:', error);
            return of(
              CompanyContactsActions.deleteCompanyContactFailure({ error }),
            );
          }),
        ),
      ),
    ),
  );
}
